import React, { useEffect, useState } from "react";
import { Redirect, RouteComponentProps } from "react-router-dom";
import DocumentMeta from "react-document-meta";
import { CircularProgress, makeStyles, Grid, Theme } from "@material-ui/core";

import { ServerResponse } from "../../services/server/OldWebClient";
import { PremiumSermonSeriesService } from "../../services/PremiumSermonSeriesService";
import { PremiumSermonService } from "../../services/PremiumSermonService";
import { MarketingPageType } from "./types/MarketingPageType";
import { PremiumSermonSeries } from "../../entities/PremiumSermonSeries";
import { ApplicationUser } from "../../entities/ApplicationUser";
import { PremiumSermon } from "../../entities/PremiumSermon";
import { ProductPageContent } from "./components/productPageContent";
import { ServerFailed } from "../errors/serverFailed";
import replaceAllCompat from "../../utilities/replaceAllCompat";
import { routes } from "../../routes";

const useStyles = makeStyles((theme: Theme) => ({
	loadingContainer: {
		marginLeft: "auto",
		marginRight: "auto",
		display: "flex",
	},
	loading: {
		marginTop: theme.spacing(4),
		marginBottom: theme.spacing(1),
	},
}));

interface Props extends RouteComponentProps<{ name: string }> {
	pageType: MarketingPageType;
	user: ApplicationUser | null;
}

export function Product({ user, pageType, match }: Props) {
	const classes = useStyles();
	const assetNameFromUri = replaceAllCompat(replaceAllCompat(match.params.name, "-", " "), "_", "-");
	const assetName = replaceAllCompat(decodeURIComponent(assetNameFromUri), "&per&", "%");
	const [series, setSeries] = useState<PremiumSermonSeries | undefined>(undefined);
	const [sermon, setSermon] = useState<PremiumSermon | undefined>(undefined);
	const [serverError, setServerError] = useState<boolean>(false);
	const [loading, setLoading] = useState(false);
	const [documentMetaInfo, setDocumentMetaInfo] = useState({
		title: "loading...",
		description: "loading info...",
	});

	const getPremiumSeriesData = async () => {
		setServerError(false);
		setLoading(true);
		let response = await PremiumSermonSeriesService.getByName(assetName);
		setLoading(false);
		if (ServerResponse.isSuccess(response)) {
			let series = response.data;
			setDocumentMetaInfo({
				title: series.name,
				description: series.description,
			});
			setSeries(series);
		} else {
			setServerError(true);
		}
	};

	const getPremiumSermonData = async () => {
		setServerError(false);
		setLoading(true);
		let response = await PremiumSermonService.getByName(assetName);
		setLoading(false);

		if (ServerResponse.isSuccess(response)) {
			let sermon = response.data;
			setDocumentMetaInfo({
				title: sermon.name,
				description: sermon.description,
			});
			setSermon(sermon);
		} else {
			setServerError(true);
		}
	};

	const renderLoadingIcon = () => {
		return (
			<div className={classes.loadingContainer}>
				<CircularProgress className={classes.loading} size={35} />
			</div>
		);
	};

	const renderRedirect = () => {
		if (pageType === MarketingPageType.Series && series !== undefined) {
			return <Redirect to={`/premium-series/${series.id}`} />;
		} else if (pageType === MarketingPageType.Sermons && sermon !== undefined) {
			return <Redirect to={`/premium-sermons/${sermon.id}`} />;
		}
	};

	const renderPageContent = () => {
		if (pageType === MarketingPageType.Series && series !== undefined) {
			let callToActionUrl = user !== null ? routes.app.resolve.activatePage(`?premiumSeriesId=${series.id}`) : routes.public.resolve.reqisterPage(`?premiumSeriesId=${series.id}`);
			return <ProductPageContent pageType={pageType} callToActionUrl={callToActionUrl} asset={series} />;
		} else if (pageType === MarketingPageType.Sermons && sermon !== undefined) {
			let callToActionUrl = user !== null ? routes.app.resolve.activatePage(`?premiumSermonId=${sermon.id}`) : routes.public.resolve.reqisterPage(`?premiumSermonId=${sermon.id}`);
			return <ProductPageContent pageType={pageType} callToActionUrl={callToActionUrl} asset={sermon} />;
		}
	};

	useEffect(() => {
		if (pageType === MarketingPageType.Series) {
			getPremiumSeriesData();
		} else if (pageType === MarketingPageType.Sermons) {
			getPremiumSermonData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<DocumentMeta {...documentMetaInfo}>
			<Grid container direction="column">
				{user && user.makerSubscription && renderRedirect()}
				{loading && renderLoadingIcon()}
				{(series !== undefined || sermon !== undefined) && renderPageContent()}
				{serverError && <ServerFailed />}
			</Grid>
		</DocumentMeta>
	);
}